import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Minimize2, BarChart2, ArrowLeft, ChevronLeft, ChevronRight, List, Filter as FilterIcon, RefreshCw } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { usePageRefresh } from '../../contexts/RefreshContext';
import StandingsTab from '../../tournaments/tabs/StandingsTab';
import LiveGameTab from '../../tournaments/tabs/LiveGameTab';
import MatchDetailPanel from './MatchDetailPanel';

/**
 * "Full screen" for the generated schedule — an in-app maximize (fills the
 * viewport, hides the site header/nav) rather than the real browser
 * Fullscreen API, so it stays forgiving on mobile Safari and works the same
 * whether or not the browser granted a fullscreen permission prompt.
 *
 * `children` is GeneratedScheduleStep's own schedule display (the
 * `ScheduleResultView` card) — it stays mounted THE WHOLE TIME this overlay
 * is open, just hidden with a plain CSS class while the Standings sub-view
 * is showing (`subView === 'standings'`), never conditionally rendered.
 * That's what makes "open Standings and safely go back to Schedule without
 * losing your current view" actually true — MatchTable's collapsed-round
 * set, any open Match Detail modal, scroll position, all of it lives inside
 * `children` and is untouched by switching `subView`, since `children`
 * itself never unmounts. The Standings sub-view embeds the same real
 * StandingsTab the tournament's own Standings tab uses — not a reimplementation.
 *
 * `onRefresh` overrides the page-level pull-to-refresh (see RefreshContext)
 * for as long as this overlay is open: without this, pulling down at the top
 * of the schedule list here still triggered the OUTER page's handler
 * (TournamentDetailPage's `window.location.reload()`), which blew away the
 * fullscreen overlay itself along with the rest of the page state — a pull
 * gesture inside a "fullscreen" view reloading the whole browser reads as a
 * bug, not a refresh. Registering here re-fetches this schedule's live data
 * in place instead, and RefreshContext's stack restores the page's own
 * handler automatically once this unregisters (overlay closes).
 *
 * A third sub-view, 'game', covers two states inside fullscreen:
 * - `detailRow` set, `game` null — Previous/Next landed on a match; shows
 *   the SAME MatchDetailPanel.jsx content MatchDetailModal.jsx uses
 *   elsewhere (round/status/schedule/court/teams), not a bare prompt —
 *   browsing via Previous/Next doesn't imply you want to score every match
 *   you pass, could just be checking details, so this never calls
 *   startScoring on its own (see GeneratedScheduleStep's goToAdjacentGame).
 * - `game` set — the actual live scoring (LiveGameTab/GameComponent),
 *   entered only via this panel's own Start Scoring/View Live Game button,
 *   or directly via Start Scoring/a Live row clicked from the embedded
 *   schedule list itself (skips the detail step there too, same as
 *   MatchRow.jsx's own row click does outside fullscreen).
 * Either way this never leaves fullscreen — that's the whole point.
 */
const FullscreenScheduleOverlay = ({
  isOpen,
  onClose,
  title,
  children,
  onRefresh,
  tournament,
  tournamentVO,
  currentUserId,
  game,
  detailRow,
  onDetailStartScoring,
  onDetailOpenLive,
  onCloseGame,
  onGameComplete,
  hasPrevGame,
  hasNextGame,
  onPrevGame,
  onNextGame,
  onOpenFilters,
  activeFilterCount = 0,
}) => {
  const { theme } = useTheme();
  const [subView, setSubView] = useState('schedule');
  const scrollContainerRef = useRef(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Bumped on every manual refresh to force StandingsTab to remount and
  // refetch — it only loads on mount (keyed off tournament.id, which never
  // changes here), so there's no other way to make it re-fetch in place.
  const [standingsKey, setStandingsKey] = useState(0);

  // Schedule/Standings/Game all share ONE scrollable container (see the
  // doc comment below on why `children` never unmounts) — without this, a
  // container left scrolled down while reviewing a completed game's summary
  // stayed at that same scrollTop once subView flipped back to 'schedule',
  // landing the schedule list at an arbitrary, often-nonsensical offset
  // (rows clipped top and bottom, sometimes past the new content's actual
  // scrollHeight entirely, which some mobile browsers render as "stuck" —
  // no amount of touch-scrolling fixes an already out-of-range scrollTop).
  useEffect(() => {
    scrollContainerRef.current?.scrollTo(0, 0);
  }, [subView]);

  useEffect(() => {
    if (isOpen) setSubView('schedule');
  }, [isOpen]);

  // A game starting/resuming, or Previous/Next landing on a row's detail
  // view — either way always means "show it", the one thing allowed to
  // override whatever sub-view the user was already looking at (e.g.
  // tapping Start Scoring while on Standings should still jump straight to
  // the game).
  useEffect(() => {
    if (game || detailRow) setSubView('game');
  }, [game, detailRow]);

  // The reverse: both clearing while 'game' is the active sub-view means
  // "Back to List" or a completed game auto-closing (see
  // handleFullscreenGameComplete) — either way there's nothing left to show
  // under 'game' anymore, so fall back to the schedule list rather than a
  // blank body.
  useEffect(() => {
    if (!game && !detailRow && subView === 'game') setSubView('schedule');
  }, [game, detailRow, subView]);

  usePageRefresh(isOpen ? onRefresh : null);

  if (!isOpen) return null;

  // The explicit header button — same underlying refetch pull-to-refresh
  // uses (see usePageRefresh above), but reachable without a touch gesture
  // (mouse/desktop has no pull-to-refresh). Refreshes schedule/rounds/match
  // statuses/scores via onRefresh, plus remounts Standings so it re-fetches
  // too, regardless of which sub-view is currently active.
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await onRefresh?.();
    } finally {
      setStandingsKey((k) => k + 1);
      setIsRefreshing(false);
    }
  };

  const backToList = () => {
    onCloseGame?.();
    setSubView('schedule');
  };

  // game is the { label, fullLabel, ... } payload useScheduleBuilder's
  // openLiveTabForRow builds — fullLabel over label since this bar has room
  // for the real team/player names, unlike the page-level tab chip label is
  // truncated for. detailRow is a raw hydrated schedule row with no
  // pre-built label of its own — construct the same "A vs B" shape from its
  // liveData (already untruncated).
  const currentLabel = game
    ? (game.fullLabel || game.label)
    : detailRow
      ? `${detailRow.liveData?.participant_a_name || 'Team A'} vs ${detailRow.liveData?.participant_b_name || 'Team B'}`
      : '';

  return createPortal(
    // z-[55]: above MobileNavFooter.jsx's fixed bottom nav (z-50, mobile-only)
    // so "fullscreen" actually covers the site chrome as its name implies —
    // without this, the nav painted on top of this overlay's own bottom
    // content, hiding whatever scrolled to the very end on mobile — but
    // still below MatchDetailModal's z-[60], so opening a match's detail
    // from inside fullscreen still layers correctly on top of it.
    <div className={`fixed inset-0 z-[55] flex flex-col ${theme.background}`}>
      <div
        className={`flex shrink-0 flex-col gap-2 border-b ${theme.border} px-4 pb-3`}
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 0.75rem)' }}
      >
        <div className={`min-w-0 truncate text-sm font-bold ${theme.text}`}>{title}</div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            aria-label="Refresh schedule, scores, and standings"
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${theme.buttonGhost} border ${theme.border} disabled:opacity-60`}
          >
            <RefreshCw size={13} className={isRefreshing ? 'animate-spin' : ''} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
          {subView === 'schedule' && onOpenFilters && (
            <button
              type="button"
              onClick={onOpenFilters}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${theme.buttonGhost} border ${theme.border}`}
            >
              <FilterIcon size={13} />
              Filters
              {activeFilterCount > 0 && (
                <span className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-bold ${theme.buttonPrimary}`}>
                  {activeFilterCount}
                </span>
              )}
            </button>
          )}
          {subView === 'standings' ? (
            <button
              type="button"
              onClick={() => setSubView((game || detailRow) ? 'game' : 'schedule')}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${theme.buttonGhost} border ${theme.border}`}
            >
              <ArrowLeft size={13} />
              {(game || detailRow) ? 'Back to Game' : 'Back to Schedule'}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setSubView('standings')}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${theme.buttonGhost} border ${theme.border}`}
            >
              <BarChart2 size={13} />
              Standings
            </button>
          )}
          {subView === 'game' && (
            <button
              type="button"
              onClick={backToList}
              className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${theme.buttonGhost} border ${theme.border}`}
            >
              <List size={13} />
              Back to List
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Exit Fullscreen"
            className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${theme.buttonGhost} border ${theme.border}`}
          >
            <Minimize2 size={13} />
            <span className="hidden sm:inline">Exit Fullscreen</span>
          </button>
        </div>
      </div>

      {subView === 'game' && (game || detailRow) && (
        // Icon-only Previous/Next — a text label here (see git history) left
        // barely any room for currentLabel, which is exactly the content
        // this bar exists to show, so it was the one getting truncated. A
        // chevron alone reads fine for "previous/next" either side of a
        // centered label; aria-label carries what the icon doesn't.
        <div className={`flex shrink-0 items-center gap-2 border-b ${theme.border} px-3 py-2`}>
          <button
            type="button"
            onClick={onPrevGame}
            disabled={!hasPrevGame}
            aria-label="Previous match"
            className={`flex shrink-0 items-center justify-center rounded-lg p-1.5 transition-colors ${theme.buttonGhost} border ${theme.border} disabled:opacity-40`}
          >
            <ChevronLeft size={15} />
          </button>
          <span className={`min-w-0 flex-1 text-center text-xs font-medium ${theme.textMuted}`}>{currentLabel}</span>
          <button
            type="button"
            onClick={onNextGame}
            disabled={!hasNextGame}
            aria-label="Next match"
            className={`flex shrink-0 items-center justify-center rounded-lg p-1.5 transition-colors ${theme.buttonGhost} border ${theme.border} disabled:opacity-40`}
          >
            <ChevronRight size={15} />
          </button>
        </div>
      )}

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        <div className={subView === 'schedule' ? 'p-4' : 'hidden'}>{children}</div>
        {subView === 'standings' && (
          <div className="p-4">
            <StandingsTab key={standingsKey} tournament={tournament} activeTab="standings" />
          </div>
        )}
        {subView === 'game' && game && (
          <LiveGameTab
            key={game.gameId}
            gameId={game.gameId}
            gameCommonId={game.gameCommonId}
            sportName={game.sportName}
            teamsData={game.teamsData}
            onClose={backToList}
            onGameComplete={onGameComplete}
          />
        )}
        {subView === 'game' && !game && detailRow && (
          <MatchDetailPanel
            row={detailRow}
            tournamentVO={tournamentVO}
            currentUserId={currentUserId}
            onStartScoring={onDetailStartScoring}
            onOpenLive={onDetailOpenLive}
            onBack={backToList}
          />
        )}
      </div>
    </div>,
    document.body
  );
};

export default FullscreenScheduleOverlay;
